import {
  CalendarArrowDownSVG,
  CalendarArrowUpSVG,
  CalendarSVG,
} from "@/assets/svg";
import { ChevronDownSVG } from "@/assets/svg/ExportIcons";
import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useLayoutEffect,
} from "react";
import ReactDOM from "react-dom";

interface DatePickerProps {
  label: string;
  id?: string;
  value: string;
  defaultMonth?: string;
  onChange: (value: string) => void;
  error?: string;
  required?: boolean;
  placeholder?: string;
  readOnly?: boolean;
  minDate?: string; // YYYY-MM-DD format
  maxDate?: string; // YYYY-MM-DD format
  containerClassName?: string;
  className?: string;
  labelClassName?: string;
  showAbove?: boolean; // Force the calendar to show above the input
  clearable?: boolean; // Whether to show clear button when hovering over a field with value
}

const DatePicker: React.FC<DatePickerProps> = ({
  label,
  id,
  value,
  defaultMonth = "",
  onChange,
  error,
  required = false,
  placeholder = "Select date",
  readOnly = false,
  minDate,
  maxDate,
  containerClassName = "",
  className = "",
  labelClassName = "",
  showAbove = false,
  clearable = false, // Default to true for backward compatibility
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [currentMonth, setCurrentMonth] = useState<Date>(
    value ? new Date(value) : defaultMonth ? new Date(defaultMonth) : new Date()
  );
  const [showYearSelector, setShowYearSelector] = useState(false);
  const [showMonthSelector, setShowMonthSelector] = useState(false);
  const [shouldShowAbove, setShouldShowAbove] = useState(showAbove);
  const datePickerRef = useRef<HTMLDivElement>(null);
  const yearSelectorRef = useRef<HTMLDivElement>(null);
  const monthSelectorRef = useRef<HTMLDivElement>(null);
  const calendarRef = useRef<HTMLDivElement>(null);
  const [calendarStyle, setCalendarStyle] = useState<React.CSSProperties>({});

  // Generate a range of years (from current year - 100 to current year + 20)
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 121 }, (_, i) => currentYear - 100 + i);

  // Determine if calendar should show above based on position in viewport
  const checkPosition = useCallback(() => {
    if (!datePickerRef.current || !calendarRef.current) return;

    const inputRect = datePickerRef.current.getBoundingClientRect();
    const calendarHeight = calendarRef.current.offsetHeight;
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - inputRect.bottom;
    const spaceAbove = inputRect.top;

    // If there's not enough space below and more space above, show above
    const shouldShow = spaceBelow < calendarHeight && spaceAbove > spaceBelow;
    setShouldShowAbove(showAbove || shouldShow);
  }, [showAbove]);

  // Check position when opening the calendar
  useEffect(() => {
    if (isOpen) {
      checkPosition();
    }
  }, [isOpen, checkPosition]);

  // Portal calendar positioning
  useLayoutEffect(() => {
    if (isOpen && datePickerRef.current) {
      const inputRect = datePickerRef.current.getBoundingClientRect();
      // const width = Math.round(inputRect.width);
      let style: React.CSSProperties = {
        position: "absolute",
        left: Math.round(inputRect.left),
        // width,
        zIndex: 9999,
      };
      if (shouldShowAbove) {
        style.bottom = window.innerHeight - Math.round(inputRect.top);
      } else {
        style.top = Math.round(inputRect.bottom);
      }
      setCalendarStyle(style);
    }
  }, [isOpen, shouldShowAbove]);

  // Close calendar when clicking outside (support portal)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const inputEl = datePickerRef.current;
      const popupEl = calendarRef.current;
      if (
        inputEl &&
        !inputEl.contains(event.target as Node) &&
        popupEl &&
        !popupEl.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setShowYearSelector(false);
        setShowMonthSelector(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Format date as YYYY-MM-DD for input value
  const formatDateForInput = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Format date as MM/DD/YYYY for display
  const formatDateForDisplay = (dateString: string): string => {
    if (!dateString) return "";
    const [year, month, day] = dateString.split("-");
    return `${month}/${day}/${year}`;
  };

  // Get days in month
  const getDaysInMonth = (year: number, month: number): number => {
    return new Date(year, month + 1, 0).getDate();
  };

  // Get day of week (0-6) for first day of month
  const getFirstDayOfMonth = (year: number, month: number): number => {
    return new Date(year, month, 1).getDay();
  };

  // Generate calendar days
  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();

    const daysInMonth = getDaysInMonth(year, month);
    const firstDayOfMonth = getFirstDayOfMonth(year, month);

    // Get days from previous month
    const daysInPrevMonth = getDaysInMonth(year, month - 1);
    const prevMonthDays = Array.from({ length: firstDayOfMonth }, (_, i) => ({
      day: daysInPrevMonth - firstDayOfMonth + i + 1,
      month: month - 1,
      year: month === 0 ? year - 1 : year,
      isCurrentMonth: false,
    }));

    // Current month days
    const currentMonthDays = Array.from({ length: daysInMonth }, (_, i) => ({
      day: i + 1,
      month,
      year,
      isCurrentMonth: true,
    }));

    // Calculate how many days we need from next month
    const totalDaysShown = 42; // 6 rows of 7 days
    const nextMonthDays = Array.from(
      {
        length:
          totalDaysShown - (prevMonthDays.length + currentMonthDays.length),
      },
      (_, i) => ({
        day: i + 1,
        month: month + 1,
        year: month === 11 ? year + 1 : year,
        isCurrentMonth: false,
      })
    );

    return [...prevMonthDays, ...currentMonthDays, ...nextMonthDays];
  };

  // Navigate to previous month
  const goToPreviousMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1)
    );
  };

  // Navigate to next month
  const goToNextMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1)
    );
  };

  // Check if a date is within the allowed range
  const isDateInRange = (year: number, month: number, day: number): boolean => {
    const date = new Date(year, month, day);
    date.setHours(0, 0, 0, 0);

    let isAfterMinDate = true;
    let isBeforeMaxDate = true;

    if (minDate) {
      const [minY, minM, minD] = minDate.split("-").map(Number);
      const minDateObj = new Date(minY, (minM || 1) - 1, minD || 1);
      minDateObj.setHours(0, 0, 0, 0);
      isAfterMinDate = date >= minDateObj;
    }

    if (maxDate) {
      const [maxY, maxM, maxD] = maxDate.split("-").map(Number);
      const maxDateObj = new Date(maxY, (maxM || 1) - 1, maxD || 1);
      maxDateObj.setHours(0, 0, 0, 0);
      isBeforeMaxDate = date <= maxDateObj;
    }

    return isAfterMinDate && isBeforeMaxDate;
  };

  // Handle date selection
  const handleDateSelect = (year: number, month: number, day: number) => {
    if (!isDateInRange(year, month, day)) return;

    const selectedDate = new Date(year, month, day);
    onChange(formatDateForInput(selectedDate));
    setIsOpen(false);
  };

  // Go to today
  const goToToday = () => {
    const today = new Date();
    const todayYear = today.getFullYear();
    const todayMonth = today.getMonth();
    const todayDate = today.getDate();

    // Only select today if it's within the allowed range
    if (isDateInRange(todayYear, todayMonth, todayDate)) {
      setCurrentMonth(new Date(todayYear, todayMonth, 1));
      handleDateSelect(todayYear, todayMonth, todayDate);
    }
  };

  // Check if a date is selected
  const isDateSelected = (
    year: number,
    month: number,
    day: number
  ): boolean => {
    if (!value) return false;
    const selectedDate = new Date(value);
    return (
      selectedDate.getFullYear() === year &&
      selectedDate.getMonth() === month &&
      selectedDate.getDate() === day
    );
  };

  // Month names for display
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  // Handle year selection
  const handleYearChange = (year: number) => {
    setCurrentMonth(new Date(year, currentMonth.getMonth(), 1));
    setShowYearSelector(false);
  };

  // Handle month selection
  const handleMonthChange = (month: number) => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), month, 1));
    setShowMonthSelector(false);
  };

  // Scroll to selected year when year selector opens
  useEffect(() => {
    if (showYearSelector && yearSelectorRef.current) {
      const selectedYearElement = yearSelectorRef.current.querySelector(
        `.year-${currentMonth.getFullYear()}`
      );
      if (selectedYearElement) {
        selectedYearElement.scrollIntoView({ block: "center" });
      }
    }
  }, [showYearSelector, currentMonth]);

  // Scroll to selected month when month selector opens
  useEffect(() => {
    if (showMonthSelector && monthSelectorRef.current) {
      const selectedMonthElement = monthSelectorRef.current.querySelector(
        `.month-${currentMonth.getMonth()}`
      );
      if (selectedMonthElement) {
        selectedMonthElement.scrollIntoView({ block: "center" });
      }
    }
  }, [showMonthSelector, currentMonth]);

  return (
    <div className={`${containerClassName || "mb-4"}`}>
      {label && (
        <label
          htmlFor={id}
          className="block text-[#1E1E1E] text-[14px] font-[500] mb-1"
        >
          {label}
          {required && <span className="text-[#DC2626]">*</span>}
        </label>
      )}
      <div className="relative sm:static" ref={datePickerRef}>
        <div
          className={`w-full h-[42px] px-3 border ${
            error ? "border-[#FF3B30]" : "border-[#E0E0E0]"
          } rounded-[6px] flex items-center justify-between  ${
            readOnly ? "" : "cursor-pointer"
          } ${className}`}
          onClick={() => !readOnly && setIsOpen(!isOpen)}
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
        >
          <span
            className={`text-[#1E1E1E] font-[400] text-[14px] ${
              value ? "" : "text-[#B3B3B3]"
            } ${labelClassName}`}
          >
            {value ? formatDateForDisplay(value) : placeholder}
          </span>
          {!readOnly && (
            <div className="flex items-center">
              {clearable && value && isHovering ? (
                <button
                  type="button"
                  className="text-gray-400 hover:text-gray-600 focus:outline-none cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange("");
                  }}
                  aria-label="Clear date"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              ) : (
                <CalendarSVG />
              )}
            </div>
          )}
        </div>

        {isOpen &&
          typeof window !== "undefined" &&
          ReactDOM.createPortal(
            <div
              ref={calendarRef}
              className="bg-white w-[260px] border border-gray-200 shadow-lg rounded z-50"
              style={calendarStyle}
            >
              <div className="p-4 flex flex-col gap-4">
                <div className="flex justify-between items-center mb-2">
                  <div className="flex items-center gap-1">
                    {/* Month selector */}
                    <div className="relative">
                      <button
                        type="button"
                        className="font-bold text-black cursor-pointer text-xs hover:bg-gray-100 px-1 py-0.5 rounded"
                        onClick={() => {
                          setShowMonthSelector(!showMonthSelector);
                          setShowYearSelector(false);
                        }}
                      >
                        {monthNames[currentMonth.getMonth()]}{" "}
                        <ChevronDownSVG className="inline w-3 h-3 text-black" />
                      </button>
                      {showMonthSelector && (
                        <div
                          ref={monthSelectorRef}
                          className="absolute z-50 mt-1 bg-white border border-gray-200 shadow-lg rounded max-h-48 overflow-y-auto w-32"
                        >
                          {monthNames.map((month, index) => (
                            <button
                              key={month}
                              type="button"
                              className={`w-full text-left px-2 py-1 text-xs cursor-pointer month-${index} ${
                                index === currentMonth.getMonth()
                                  ? "bg-[#73B5C5] text-white"
                                  : "hover:bg-gray-100"
                              }`}
                              onClick={() => handleMonthChange(index)}
                            >
                              {month}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Year selector */}
                    <div className="relative">
                      <button
                        type="button"
                        className="font-bold text-black cursor-pointer text-xs hover:bg-gray-100 px-1 py-0.5 rounded"
                        onClick={() => {
                          setShowYearSelector(!showYearSelector);
                          setShowMonthSelector(false);
                        }}
                      >
                        {currentMonth.getFullYear()}{" "}
                        <ChevronDownSVG className="inline w-3 h-3 text-black" />
                      </button>
                      {showYearSelector && (
                        <div
                          ref={yearSelectorRef}
                          className="absolute z-50 mt-1 bg-white border border-gray-200 shadow-lg rounded max-h-48 overflow-y-auto w-20"
                        >
                          {years.map((year) => (
                            <button
                              key={year}
                              type="button"
                              className={`w-full text-left px-2 py-1 text-xs cursor-pointer year-${year} ${
                                year === currentMonth.getFullYear()
                                  ? "bg-[#73B5C5] text-white"
                                  : "hover:bg-gray-100"
                              }`}
                              onClick={() => handleYearChange(year)}
                            >
                              {year}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center">
                    <button
                      type="button"
                      className="p-1 hover:bg-gray-100 rounded-full cursor-pointer"
                      onClick={goToPreviousMonth}
                    >
                      <CalendarArrowUpSVG />
                    </button>
                    <button
                      type="button"
                      className="p-1 hover:bg-gray-100 rounded-full cursor-pointer"
                      onClick={goToNextMonth}
                    >
                      <CalendarArrowDownSVG />
                    </button>
                  </div>
                </div>

                <div>
                  <div className="grid grid-cols-7 gap-1 text-center text-xs font-[400] text-black">
                    <div>S</div>
                    <div>M</div>
                    <div>T</div>
                    <div>W</div>
                    <div>T</div>
                    <div>F</div>
                    <div>S</div>
                  </div>

                  <div className="grid grid-cols-7 gap-1 mt-1">
                    {generateCalendarDays().map((dateObj, index) => (
                      <button
                        key={index}
                        type="button"
                        className={`
                      h-8 w-8 text-sm flex items-center justify-center 
                      ${
                        !isDateInRange(dateObj.year, dateObj.month, dateObj.day)
                          ? "text-gray-300 cursor-not-allowed"
                          : dateObj.isCurrentMonth
                          ? isDateSelected(
                              dateObj.year,
                              dateObj.month,
                              dateObj.day
                            )
                            ? "bg-[#73B5C5] text-white cursor-pointer"
                            : "hover:bg-gray-100 text-black cursor-pointer"
                          : "text-black opacity-[38%] cursor-pointer"
                      }
                    `}
                        onClick={() =>
                          isDateInRange(
                            dateObj.year,
                            dateObj.month,
                            dateObj.day
                          ) &&
                          handleDateSelect(
                            dateObj.year,
                            dateObj.month,
                            dateObj.day
                          )
                        }
                      >
                        {dateObj.day}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="text-left">
                  <button
                    type="button"
                    className="text-[#0275FF] text-xs hover:underline cursor-pointer"
                    onClick={goToToday}
                  >
                    Today
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )}
      </div>
      {error && (
        <p className="mt-0 text-[12px] font-[500] text-[#FF3B30]">{error}</p>
      )}
    </div>
  );
};

export default DatePicker;
